/**
 * @typedef {import('@/epos/vue/modals/TodayViewFilters/types.js').FilterValues} FilterValues
 * @typedef {import('@/lib/Sessions/SessionEvent')} SessionEvent
 * @typedef {import('@/lib/Categories/Category')} Category
 * @typedef {import('@/lib/Tickets/Ticket')} Ticket
 * @typedef {import('@/lib/DeviceGroups/DeviceGroup')} DeviceGroup
 */

import { SpacesAvailableModeEnum } from '@/epos/vue/pages/today/SpacesAvailableModeEnum';

/**
 * Run some filters on the supplied array of sessions.
 * This only applies filters that can be done before we have the availability.
 *
 * @param {Session[]} sessions
 * @param {{eventID: ?number, status: ?string, spacesAvailable: number, eventIDsFilteredByCategories: ?number[]}} filters
 *
 * @return {Session[]}
 */
export const filterSessionsForTodayViewBeforeAvailability = (sessions, filters) => {
    // If there are no applicable eventIDs at this point return early.
    if (!filters.eventIDs || filters.eventIDs.length === 0) {
        return [];
    }

    return sessions.filter((session) => {
        if (!filters.eventIDs.includes(session.eventID)) {
            // Doesn't match any of the filtered events.
            return false;
        }

        if (filters.status && session.status !== filters.status) {
            // Doesn't match the status filter.
            return false;
        }

        return true;
    });
};

/**
 * Run some filters on the supplied array of sessions.
 * This only applies filters that depend on the availability.
 *
 * @param {Session[]} sessions
 * @param {Object.<number, SessionAvailability>} sessionAvailabilities
 * @param {{eventID: ?number, status: ?string, spacesAvailable: number, spacesAvailableMode: string}} filters
 *
 * @return {Session[]}
 */
export const filterSessionsForTodayViewAfterAvailability = (sessions, sessionAvailabilities, filters) => {
    return sessions.filter((session) => {
        if (filters.spacesAvailableMode === SpacesAvailableModeEnum.MIN && filters.spacesAvailable === 0) {
            // If this is the default filter (at least 0 spaces available) just return true.
            // If we didn't do this then there is a side effect of hiding sessions if we haven't been able to load
            // their space numbers. That's an undesirable side effect.
            return true;
        }

        const availability = sessionAvailabilities[session.ID];
        if (!availability) {
            // Fail safely and show the session if the availability is not yet loaded.
            return true;
        }

        if (filters.spacesAvailableMode === SpacesAvailableModeEnum.MIN) {
            if (availability.getSpacesBookable() < filters.spacesAvailable) {
                return false;
            }
        } else if (filters.spacesAvailableMode === SpacesAvailableModeEnum.EXACT) {
            if (availability.getSpacesBookable() !== filters.spacesAvailable) {
                return false;
            }
        }

        return true;
    });
};

/**
 * @param {Session[]} sessions
 * @param {Object.<number, SessionAvailability>} sessionAvailabilities
 *
 * @return {Session[]}
 */
export const filterSoldOutSessions = (sessions, sessionAvailabilities) => {
    return sessions.filter((session) => {
        const availability = sessionAvailabilities[session.ID];
        if (!availability) {
            // Fail safely and show the session if the availability is not yet loaded.
            return true;
        }

        return availability.getSpacesBookable() > 0;
    });
};

/**
 * Normalize and filter the eventIDs.
 *
 * @param {FilterValues} filters
 * @param {number[]} allEventIDs
 * @param {number[]} activeEventIDs
 *
 * @return {FilterValues}
 */
export const filterEventIDs = (filters, allEventIDs, activeEventIDs) => {
    // If the user hasn't filtered to show only specific events show them all.
    if (!filters.eventIDs || filters.eventIDs.length === 0) {
        filters.eventIDs = allEventIDs;
    }

    // Filter to remove Inactive events.
    if (filters.hideInactiveCategoriesAndEvents) {
        filters.eventIDs = filters.eventIDs.filter((eventID) => {
            return activeEventIDs.some((activeEventID) => activeEventID === eventID);
        });
    }

    return filters;
};

/**
 * Normalize and filter the categoryIDs.
 *
 * @param {FilterValues} filters
 * @param {number[]} allCategoryIDs
 * @param {number[]} activeCategoryIDs
 *
 * @return {FilterValues}
 */
export const filterCategoryIDs = (filters, allCategoryIDs, activeCategoryIDs) => {
    // If the user hasn't filtered to show only specific categories show them all.
    if (!filters.categoryIDs || filters.categoryIDs.length === 0) {
        filters.categoryIDs = allCategoryIDs;
    }

    // Filter to remove Inactive categories.
    if (filters.hideInactiveCategoriesAndEvents) {
        filters.categoryIDs = filters.categoryIDs.filter((categoryID) => {
            return activeCategoryIDs.some((activeCategoryID) => activeCategoryID === categoryID);
        });
    }

    return filters;
};

/**
 * Returns true if the given event is visible per the device group's visible items configuration.
 * Mirrors the OR semantics used on the Sell screen.
 *
 * @param {SessionEvent} event
 * @param {Ticket[]} ticketsForEvent
 * @param {DeviceGroup|null} deviceGroup
 *
 * @return {boolean}
 */
export const isEventVisibleForDeviceGroup = (event, ticketsForEvent, deviceGroup) => {
    if (!deviceGroup?.hasVisibleItems()) {
        return true;
    }
    const { visibleCatIDs, visibleEventIDs, visibleItemIDs } = deviceGroup;

    if (visibleCatIDs.length && event.ticketCats?.some((tc) => visibleCatIDs.includes(tc.catID))) {
        return true;
    }
    if (visibleEventIDs.length && visibleEventIDs.includes(event.ID)) {
        return true;
    }
    if (visibleItemIDs.length && ticketsForEvent.some((t) => visibleItemIDs.includes(t.ID))) {
        return true;
    }
    return false;
};

/**
 * Filter an array of events by the device group's visible items configuration.
 *
 * @param {SessionEvent[]} events
 * @param {Object<number, Ticket[]>} ticketsByEvent
 * @param {DeviceGroup|null} deviceGroup
 *
 * @return {SessionEvent[]}
 */
export const filterEventsByVisibleItems = (events, ticketsByEvent, deviceGroup) => {
    if (!deviceGroup?.hasVisibleItems()) {
        return events;
    }
    return events.filter(
        (event) => isEventVisibleForDeviceGroup(event, ticketsByEvent[event.ID] || [], deviceGroup),
    );
};

/**
 * Filter an array of categories by the device group's visible items configuration. A category is visible if its
 * ID is in visibleCatIDs, or it contains an event in visibleEventIDs, or it contains a ticket in visibleItemIDs.
 *
 * @param {Category[]} categories
 * @param {SessionEvent[]} events
 * @param {Object<number, Ticket[]>} ticketsByEvent
 * @param {DeviceGroup|null} deviceGroup
 *
 * @return {Category[]}
 */
export const filterCategoriesByVisibleItems = (categories, events, ticketsByEvent, deviceGroup) => {
    if (!deviceGroup?.hasVisibleItems()) {
        return categories;
    }
    const { visibleCatIDs, visibleEventIDs, visibleItemIDs } = deviceGroup;

    const visibleEventIDSet = new Set(visibleEventIDs);
    const visibleItemIDSet = new Set(visibleItemIDs);
    const visibleCategoryIDSet = new Set(visibleCatIDs);

    if (visibleEventIDSet.size || visibleItemIDSet.size) {
        events.forEach((event) => {
            if (visibleEventIDSet.has(event.ID) && event.ticketCats) {
                event.ticketCats.forEach((tc) => visibleCategoryIDSet.add(tc.catID));
            }
            if (visibleItemIDSet.size) {
                (ticketsByEvent[event.ID] || []).forEach((ticket) => {
                    if (visibleItemIDSet.has(ticket.ID)) {
                        visibleCategoryIDSet.add(ticket.catID);
                    }
                });
            }
        });
    }

    return categories.filter((category) => visibleCategoryIDSet.has(category.ID));
};

/**
 * @param {FilterValues} filters
 * @param {[]}events
 *
 * @return {FilterValues}
 */
export const filterEventIDsByCategory = (filters, events) => {
    filters.eventIDs = filters.eventIDs.filter((eventID) => {
        if (events.hasOwnProperty(eventID)) {
            const event = events[eventID];
            if (event && event.ticketCats) {
                return event.ticketCats.some((ticketCat) => filters.categoryIDs.includes(ticketCat.catID));
            }
        }
        return false;
    });

    return filters;
};
